import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useHistory, Link } from 'react-router-dom'
import {
  Box as MantineBox,
  Text, Button, Group, Badge, Modal, Stack, Textarea, TextInput,
  Select, SimpleGrid, Card, ActionIcon, NumberInput, Drawer, Popover
} from '@mantine/core'
import {
  IoArrowUpOutline, IoArrowDownOutline, IoPencilOutline, IoTrashOutline,
  IoShareOutline, IoCogOutline, IoImageOutline, IoLinkOutline,
  IoChevronDownOutline, IoChevronUpOutline,
  IoArrowDownOutline as IoArrowDown
} from 'react-icons/io5'
import { format } from 'date-fns'
import { toast } from 'react-toastify'
import LoadingComponent from '../../app/layout/LoadingComponents'
import { TagPicker, AuthGateCta, EmptyState, AmmoScoreBadge } from '../../app/common/ui'
import { useStore } from '../../app/stores/store'
import agent from '../../app/api/agent'
import { TagTargetType } from '../../app/models/tag'
import type { TagDto } from '../../app/models/tag'
import type { ContentPhotoDto } from '../../app/models/contentPhoto'
import { ContentPhotoOwnerType } from '../../app/models/contentPhoto'
import type { Part } from '../../app/models/part'
import type { PagedResult } from '../../app/models/pagedResult'
import PhotoGallery from './PhotoGallery'
import GalleryUploadButton from './GalleryUploadButton'
import PlatformSchematic from './PlatformSchematic'
import { getAkIllustrationVariant } from './akIllustration'
import AddPartPanel from './AddPartPanel'
import ZoneManagePanel from './ZoneManagePanel'
import ZoneViewModal from './ZoneViewModal'
import MountTypeSelector from './MountTypeSelector'
import PartOverlayLayer from './PartOverlayLayer'
import { useZoneGeometry } from './useZoneGeometry'
import { isMultiPartZone, getPlatformZoneOrder, isAkPlatform, getCategoryZone } from './zoneCategoryMap'
import type { BuildPartDto } from '../../app/models/build'
import { getPartDefaults } from './partDefaults'
import { useCountUp } from '../../app/hooks/useCountUp'
import { useZoomPan } from '../../app/hooks/useZoomPan'
import BuildShareCard from './BuildShareCard'
import BuildCompletionBar from './BuildCompletionBar'
import './BuildWorkbench.css'

/** Stable JSX component ref — avoids TS/tooling quirks with polymorphic `Box` (same pattern as react-icons). */
const WorkbenchBox = MantineBox
const Box = WorkbenchBox

// ── helpers ──────────────────────────────────────────────────────────────────

function fmtCurrency (v: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(v)
}

function getAmmoTier (score: number): string {
  if (score >= 1000) return 'Armorer'
  if (score >= 500) return 'Curator'
  if (score >= 200) return 'Expert'
  if (score >= 75) return 'Contributor'
  if (score >= 20) return 'Enthusiast'
  return 'Newcomer'
}

// ── AR-15 zone order (fallback; AK/other platforms use getPlatformZoneOrder) ──
const ZONE_ORDER = [
  'Muzzle Device', 'Barrel', 'Handguard / Rail', 'Bolt Carrier Group',
  'Charging Handle', 'Upper Receiver', 'Optic', 'Light / Laser',
  'Trigger Group', 'Lower Receiver', 'Pistol Grip', 'Stock / Brace',
  'Sling / Mount', 'Other'
]

// ── Component ─────────────────────────────────────────────────────────────────
export default observer(function BuildWorkbench () {
  const { buildStore, userStore, voteStore } = useStore()
  const { id } = useParams<{ id: string }>()
  const history = useHistory()

  // Derived from store — declared before any hooks that reference these values
  const build = buildStore.selectedBuild
  const isOwner = !!userStore.user && userStore.user.username === build?.ownerUsername
  const canManage = isOwner || !!userStore.user?.isAdmin || !!userStore.user?.isModerator

  // UI state
  const [addPartOpen, setAddPartOpen] = useState(false)
  const [editDetailsOpen, setEditDetailsOpen] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(false)

  // Add-part form
  const [partSearch, setPartSearch] = useState('')
  const [partCategoryFilter, setPartCategoryFilter] = useState<string | null>(null)
  const [allParts, setAllParts] = useState<Part[]>([])
  const [partsLoading, setPartsLoading] = useState(false)
  const [selectedPartId, setSelectedPartId] = useState<string | null>(null)
  const [partNotes, setPartNotes] = useState('')
  const [partQty, setPartQty] = useState<number>(1)
  const [partZoneOverride, setPartZoneOverride] = useState<string | null>(null)
  const [addingPart, setAddingPart] = useState(false)

  // Edit-part modal
  const [editPartId, setEditPartId] = useState<string | null>(null) // buildPart.id
  const [editPartNotes, setEditPartNotes] = useState('')
  const [editPartQty, setEditPartQty] = useState<number>(1)
  const [editPartZone, setEditPartZone] = useState<string | null>(null)
  const [savingPart, setSavingPart] = useState(false)

  // Edit-details drawer
  const [editName, setEditName] = useState('')
  const [editDesc, setEditDesc] = useState('')
  const [editSubmitting, setEditSubmitting] = useState(false)

  // Animation
  const [pulsedZone, setPulsedZone] = useState<string | null>(null)

  // Zone-interaction state (Armorer's Bench Phase 1)
  const [activeZone, setActiveZone] = useState<string | null>(null)
  const [addPanelOpen, setAddPanelOpen] = useState(false)
  const [managePanelOpen, setManagePanelOpen] = useState(false)
  const [viewModalOpen, setViewModalOpen] = useState(false)

  // Mount type selector state (for AK platforms)
  const [currentMountType, setCurrentMountType] = useState<string | null>(null)

  // Share popover (Armorer's Bench Phase 3)
  const [shareOpen, setShareOpen] = useState(false)
  const [sharingImage, setSharingImage] = useState(false)
  const shareCardRef = useRef<HTMLDivElement>(null)

  // Inline title edit (Phase 3 step 10)
  const [titleEditing, setTitleEditing] = useState(false)
  const [titleDraft, setTitleDraft] = useState('')
  const [titleSaving, setTitleSaving] = useState(false)

  // Empty state & first-part animation (Phase 3 step 7)
  const [firstPartAdded, setFirstPartAdded] = useState(false)
  const prevPartCount = useRef(0)

  // Collapsible zone groups — undefined/true = expanded, false = collapsed (Phase 3 step 11)
  const [expandedZones, setExpandedZones] = useState<Record<string, boolean>>(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      const m: Record<string, boolean> = {}
      ZONE_ORDER.forEach((z) => { m[z] = false })
      return m
    }
    return {}
  })

  // Zoom/pan (Phase 3 step 9)
  const zoomWrapRef = useRef<HTMLDivElement>(null)
  const { scale: zoomScale, transform: zoomTransform, handlers: zoomHandlers, reset: resetZoom } = useZoomPan(zoomWrapRef)

  // Overlay / zone geometry (Armorer's Bench Phase 2)
  const schematicContainerRef = useRef<HTMLDivElement>(null)
  const { geometry: zoneGeometry, containerWidth, containerHeight } = useZoneGeometry(schematicContainerRef)

  // Gallery / tags
  const [tags, setTags] = useState<TagDto[]>([])
  const [photos, setPhotos] = useState<ContentPhotoDto[]>([])
  const [photosLoading, setPhotosLoading] = useState(false)

  // Load build
  useEffect(() => {
    if (id) buildStore.loadBuild(id)
    return () => buildStore.clearSelectedBuild()
  }, [id, buildStore])

  // Load photos
  const loadPhotos = useCallback(async (buildId: string) => {
    setPhotosLoading(true)
    try { setPhotos(await agent.ContentPhotos.list(ContentPhotoOwnerType.Build, buildId)) } catch { setPhotos([]) } finally { setPhotosLoading(false) }
  }, [])

  useEffect(() => { if (id) void loadPhotos(id) }, [id, loadPhotos])

  // Sync tags from store
  useEffect(() => {
    if (buildStore.selectedBuild?.tags != null) { setTags(buildStore.selectedBuild.tags ?? []) }
  }, [buildStore.selectedBuild?.tags])

  // Must be declared before effects that read it (TDZ if placed below those effects).
  const normalizedPartsByZone = useMemo(() => {
    if (!build?.partsByZone) return {} as Record<string, import('../../app/models/build').BuildPartDto[]>
    const out: Record<string, import('../../app/models/build').BuildPartDto[]> = {}
    for (const [key, parts] of Object.entries(build.partsByZone)) {
      const zone = getCategoryZone(key)
      out[zone] = out[zone] ? [...out[zone], ...(parts as BuildPartDto[])] : [...(parts as BuildPartDto[])]
    }
    return out
  }, [build?.partsByZone])

  // Sync mount type from build (for AK platforms)
  useEffect(() => {
    if (!build || !isAkPlatform(build.firearmModelPlatform)) {
      setCurrentMountType(null)
      return
    }
    // Find the Side Rail Mount part and extract its mount type
    const sideRailPart = (normalizedPartsByZone['Side Rail Mount'] ?? []).find(
      (bp:any) => bp.mountType != null
    )
    setCurrentMountType(sideRailPart?.mountType ?? null)
  }, [build, normalizedPartsByZone])

  // Open add-part → fetch all approved parts once
  useEffect(() => {
    if (!addPartOpen) return
    setPartsLoading(true)
    const already = new Set(buildStore.selectedBuild?.parts?.map((bp:any) => bp.partId) ?? [])
    agent.Parts.list({ pageSize: 500 })
      .then((r: PagedResult<Part>) =>
        setAllParts(r.items.filter((p:Part) => p.status === 1 && !already.has(p.id)))
      )
      .catch(() => setAllParts([]))
      .finally(() => setPartsLoading(false))
    setSelectedPartId(null); setPartNotes(''); setPartQty(1); setPartSearch(''); setPartCategoryFilter(null); setPartZoneOverride(null)
  }, [addPartOpen, buildStore.selectedBuild?.parts])

  // Seed edit-details drawer
  useEffect(() => {
    if (editDetailsOpen && buildStore.selectedBuild) {
      setEditName(buildStore.selectedBuild.name)
      setEditDesc(buildStore.selectedBuild.description ?? '')
    }
  }, [editDetailsOpen, buildStore.selectedBuild])

  // First part added: 0→1 transition (Phase 3 step 7)
  useEffect(() => {
    const count = buildStore.selectedBuild?.parts?.length ?? 0
    if (prevPartCount.current === 0 && count >= 1) {
      setFirstPartAdded(true)
      toast.info('First part added! Keep building.', { toastId: 'first-part', autoClose: 3000 })
      setTimeout(() => setFirstPartAdded(false), 600)
    }
    prevPartCount.current = count
  }, [buildStore.selectedBuild?.parts?.length])

  // ── Derived (build/isOwner/canManage declared at top of component) ──────────

  const totalValue = build?.parts?.reduce((s:number, bp:any) => s + (bp.part?.value ?? 0) * (bp.quantity ?? 1), 0) ?? 0
  const animatedCost = useCountUp(totalValue, 800)

  const activeZones = useMemo(
    () => Object.keys(normalizedPartsByZone).filter((z) => (normalizedPartsByZone[z]?.length ?? 0) > 0),
    [normalizedPartsByZone]
  )

  const zoneParts = useMemo(
    () => Object.fromEntries(Object.entries(normalizedPartsByZone).map(([z, parts]:any) => [z, parts.length])),
    [normalizedPartsByZone]
  )

  // Parts grouped for display (sorted by platform-specific zone order)
  const groupedParts = useMemo(() => {
    if (!build?.partsByZone) return []
    const zoneOrder = getPlatformZoneOrder(build.firearmModelPlatform)
    const inOrder = zoneOrder.filter((z) => (normalizedPartsByZone[z]?.length ?? 0) > 0)
    // Catch any parts whose zone isn't in the platform zone order (e.g. AR part on AK build)
    const knownZones = new Set(zoneOrder)
    const overflow = Object.keys(normalizedPartsByZone).filter(
      (z) => !knownZones.has(z) && (normalizedPartsByZone[z]?.length ?? 0) > 0
    )
    return [...inOrder, ...overflow].map((z) => ({ zone: z, parts: normalizedPartsByZone[z] }))
  }, [normalizedPartsByZone, build?.partsByZone, build?.firearmModelPlatform])

  // Filtered part options in add-part modal
  const filteredParts = useMemo(() => {
    let list = allParts
    if (partCategoryFilter) list = list.filter((p:Part) => p.category === partCategoryFilter)
    if (partSearch.trim()) {
      const q = partSearch.toLowerCase()
      list = list.filter((p:Part) =>
        p.name.toLowerCase().includes(q) ||
        (p.manufacturer ?? '').toLowerCase().includes(q) ||
        (p.partNumber ?? '').toLowerCase().includes(q)
      )
    }
    return list
  }, [allParts, partSearch, partCategoryFilter])

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleVote = async (value: number) => {
    if (!build) return
    if (!userStore.isLoggedIn) { history.push('/register'); return }
    if (userStore.user?.username === build.ownerUsername) return
    try {
      if (build.currentUserVote === value) { await agent.Votes.remove(agent.VoteTargetType.Build, build.id) } else { await agent.Votes.cast(agent.VoteTargetType.Build, build.id, value) }
      if (id) buildStore.loadBuild(id)
    } catch { /* interceptor */ }
  }

  const handleAddPart = async () => {
    if (!id || !selectedPartId) return
    setAddingPart(true)
    try {
      const zoneOverride = partZoneOverride || undefined
      await buildStore.addPartToBuild(id, selectedPartId, partNotes || undefined, partQty, zoneOverride)
      // Pulse the effective zone (override takes priority)
      const addedPart = allParts.find((p:Part) => p.id === selectedPartId)
      const effectiveZone = zoneOverride || addedPart?.category
      if (effectiveZone) {
        setPulsedZone(effectiveZone)
        setTimeout(() => setPulsedZone(null), 600)
      }
      setAddPartOpen(false)
    } catch { /* interceptor */ } finally { setAddingPart(false) }
  }

  const openEditPart = (bp: import('../../app/models/build').BuildPartDto) => {
    setEditPartId(bp.id)
    setEditPartNotes(bp.notes ?? '')
    setEditPartQty(bp.quantity)
    setEditPartZone(bp.zoneOverride ?? bp.part?.category ?? null)
  }

  const handleSavePart = async () => {
    if (!id || !editPartId) return
    setSavingPart(true)
    try {
      // Send null to clear override if user picked the part's own category
      const bp = build?.parts?.find((p:any) => p.id === editPartId)
      const ownCategory = bp?.part?.category ?? null
      const zoneToSend = editPartZone === ownCategory ? null : editPartZone
      await buildStore.updateBuildPart(id, editPartId, editPartNotes || undefined, editPartQty, zoneToSend || undefined)
      setEditPartId(null)
    } catch { /* interceptor */ } finally { setSavingPart(false) }
  }

  const handleRemovePart = async (partId: string) => {
    if (!id) return
    try { await buildStore.removePartFromBuild(id, partId) } catch { /* interceptor */ }
  }

  const handleDelete = async () => {
    if (!id) return
    try { await buildStore.deleteBuild(id); history.push('/builds') } catch { /* interceptor */ }
    setDeleteConfirm(false)
  }

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href)
      .then(() => { toast.success('Link copied!'); setShareOpen(false) })
      .catch(() => toast.error('Could not copy link'))
  }

  const handleDownloadImage = async () => {
    const el = shareCardRef.current
    if (!el) return
    setSharingImage(true)
    try {
      const html2canvas = (await import('html2canvas')).default
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#0A0E13' } as any)
      const link = document.createElement('a')
      link.download = `${build?.name ?? 'build'}-share.png`.replace(/\s+/g, '-').toLowerCase()
      link.href = canvas.toDataURL('image/png')
      link.click()
      setShareOpen(false)
    } catch {
      toast.error('Could not generate image')
    } finally {
      setSharingImage(false)
    }
  }

  const handleEditSubmit = async () => {
    if (!id || !editName.trim()) return
    setEditSubmitting(true)
    try {
      await buildStore.updateBuild(id, { name: editName.trim(), description: editDesc.trim() || undefined })
      toast.success('Build updated.')
      setEditDetailsOpen(false)
    } catch { /* interceptor */ } finally { setEditSubmitting(false) }
  }

  const handleGalleryDelete = async (photoId: string) => {
    if (!id) return
    try { await agent.ContentPhotos.delete(photoId); await loadPhotos(id); if (id) buildStore.loadBuild(id) } catch { /* interceptor */ }
  }

  const handleSetMain = async (photoId: string) => {
    if (!id) return
    try { await agent.ContentPhotos.setMain(photoId); await loadPhotos(id); if (id) buildStore.loadBuild(id) } catch { /* interceptor */ }
  }

  const handleZoneClick = useCallback((zone: string) => {
    if (!build) return
    if (isOwner) {
      const partsInZone: BuildPartDto[] = normalizedPartsByZone[zone] ?? []
      if (partsInZone.length === 0 || isMultiPartZone(zone)) {
        setActiveZone(zone)
        setAddPanelOpen(true)
      } else {
        setActiveZone(zone)
        setManagePanelOpen(true)
      }
    } else {
      const partsInZone: BuildPartDto[] = normalizedPartsByZone[zone] ?? []
      if (partsInZone.length > 0) {
        setActiveZone(zone)
        setViewModalOpen(true)
      }
    }
  }, [build, isOwner, normalizedPartsByZone])

  /** Reloads the build after a part is added or updated. */
  const handleBuildRefetch = useCallback(() => {
    if (id) buildStore.loadBuild(id)
  }, [id, buildStore])

  /** ZoneManagePanel "Add Another Part" → close manage, open add for same zone. */
  const handleAddAnotherPart = useCallback(() => {
    setManagePanelOpen(false)
    setAddPanelOpen(true)
    // activeZone intentionally left set so AddPartPanel opens for the same zone.
  }, [])

  /** Update mount type for the Side Rail Mount part (AK platforms only). */
  const handleMountTypeChange = useCallback(
    async (mountType: string) => {
      if (!id || !build) return
      try {
        const sideRailParts = normalizedPartsByZone['Side Rail Mount'] ?? []
        if (sideRailParts.length > 0) {
          const sideRailPart = sideRailParts[0]
          await agent.Builds.updatePart(
            id,
            sideRailPart.id,
            sideRailPart.notes,
            sideRailPart.quantity,
            sideRailPart.zoneOverride,
            undefined,
            mountType
          )
          setCurrentMountType(mountType)
          if (id) buildStore.loadBuild(id)
        }
      } catch {
        /* global interceptor */
      }
    },
    [id, build, buildStore, normalizedPartsByZone]
  )

  /** Inline title edit handlers (Phase 3 step 10) */
  const startTitleEdit = useCallback(() => {
    if (!build) return
    setTitleDraft(build.name)
    setTitleEditing(true)
  }, [build])

  const handleTitleSave = useCallback(async () => {
    if (!id || !titleDraft.trim() || !build) return
    if (titleDraft.trim() === build.name) { setTitleEditing(false); return }
    setTitleSaving(true)
    try {
      await agent.Builds.update(id, { name: titleDraft.trim() })
      await buildStore.loadBuild(id)
      setTitleEditing(false)
    } catch { /* interceptor */ } finally { setTitleSaving(false) }
  }, [id, titleDraft, build, buildStore])

  const handleTitleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { void handleTitleSave() }
    if (e.key === 'Escape') { setTitleEditing(false) }
  }, [handleTitleSave])

  /** Toggle zone expand/collapse (Phase 3 step 11) */
  const toggleZone = useCallback((zone: string) => {
    setExpandedZones((prev) => ({ ...prev, [zone]: !(prev[zone] !== false) }))
  }, [])

  /** Overlay drag-end: persist position and patch local store — no full reload. */
  const handlePartPositionChange = useCallback(async (buildPartId: string, x: number, y: number) => {
    if (!id) return
    try {
      await buildStore.updateBuildPartPosition(id, buildPartId, x, y)
      toast.success('Position saved', { autoClose: 1200, toastId: `pos-${buildPartId}` })
    } catch { /* interceptor */ }
  }, [id, buildStore])

  /** Overlay "Reset to default position" context menu action. */
  const handlePartPositionReset = useCallback(async (buildPartId: string) => {
    if (!id || !build) return
    const bp = build.parts?.find((p:any) => p.id === buildPartId)
    if (!bp) return
    const defaults = getPartDefaults(build.firearmModelPlatform, bp.part?.category)
    try {
      await buildStore.updateBuildPartPosition(id, buildPartId, defaults.position.x, defaults.position.y)
      toast.success('Position reset', { autoClose: 1200, toastId: `pos-reset-${buildPartId}` })
    } catch { /* interceptor */ }
  }, [id, build, buildStore])

  // ── Loading guard ──────────────────────────────────────────────────────────
  if (buildStore.loadingInitial || !build) return <LoadingComponent content="Loading build..." />

  const score = build.voteScore ?? 0
  const currentVote = build.currentUserVote ?? null
  const heroUrl = photos.find((p) => p.isMain)?.url ?? build.mainPhotoUrl ?? null
  const partCount = build.parts?.length ?? 0
  const zoneOrder = getPlatformZoneOrder(build.firearmModelPlatform)
  const isEmpty = partCount === 0

  const categoryOptions = [
    { value: '', label: 'All categories' },
    ...zoneOrder.map((z) => ({ value: z, label: z }))
  ]

  return (
    <Box sx={{ maxWidth: 1400, margin: '0 auto', padding: '16px 24px' }}>
      {/* Back */}
      <Text component={Link} to="/builds" size="sm" sx={{ color: 'var(--gs-muted)', display: 'block', marginBottom: 20 }}>
        ← Back to Builds
      </Text>

      {/* ── Hero image ── */}
      {heroUrl && (
        <Box component="img" src={heroUrl} alt={build.name} sx={{
          width: '100%',
          maxHeight: 420,
          objectFit: 'contain',
          background: 'var(--gs-surface2)',
          borderRadius: 8,
          marginBottom: 20,
          display: 'block'
        }} />
      )}

      {/* ══ HEADER (Phase 3 step 10) ══════════════════════════════════════════ */}
      <Box sx={{ borderBottom: '1px solid var(--gs-border)', paddingBottom: 20, marginBottom: 16 }}>
        <Group position="apart" align="flex-start" noWrap>

          {/* Left: title + model row + owner row */}
          <Box sx={{ minWidth: 0, flex: 1, marginRight: 16 }}>

            {/* Inline-editable title */}
            {isOwner && titleEditing
              ? (
              <TextInput
                value={titleDraft}
                onChange={(e:React.ChangeEvent<HTMLInputElement>) => setTitleDraft(e.currentTarget.value)}
                onBlur={() => void handleTitleSave()}
                onKeyDown={handleTitleKeyDown}
                disabled={titleSaving}
                autoFocus
                sx={{
                  marginBottom: 6,
                  '& input': {
                    fontFamily: 'Oswald, sans-serif',
                    fontWeight: 700,
                    fontSize: 'clamp(20px,2.8vw,28px)',
                    color: 'var(--gs-text)',
                    borderBottom: '2px solid var(--gs-accent)',
                    borderRadius: 0,
                    background: 'transparent'
                  }
                }}
              />
                )
              : (
              <Text
                onClick={isOwner ? startTitleEdit : undefined}
                sx={{
                  fontFamily: 'Oswald, sans-serif',
                  fontWeight: 700,
                  fontSize: 'clamp(22px,3vw,30px)',
                  color: 'var(--gs-text)',
                  lineHeight: 1.15,
                  marginBottom: 6,
                  cursor: isOwner ? 'text' : 'default',
                  '&:hover': isOwner ? { color: 'var(--gs-accent)' } : {},
                  transition: 'color 0.15s'
                }}
              >
                {build.name}
              </Text>
                )}

            {/* Model link + amber platform badge */}
            <Group spacing={8} mb={6}>
              <Text component={Link} to={`/registry/${build.firearmModelId}`} size="sm"
                sx={{ color: 'var(--gs-accent)', fontFamily: 'DM Sans, sans-serif', fontWeight: 600 }}>
                {build.firearmModelName}
              </Text>
              {build.firearmModelPlatform && (
                <Badge size="sm" sx={{
                  background: 'rgba(232,93,4,0.18)',
                  color: 'var(--gs-accent)',
                  border: '1px solid rgba(232,93,4,0.45)',
                  fontFamily: 'Oswald, sans-serif',
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                  paddingLeft: 8,
                  paddingRight: 8
                }}>
                  {build.firearmModelPlatform}
                </Badge>
              )}
            </Group>

            {/* Owner avatar + username + date + AmmoScore */}
            <Group spacing={8} align="center">
              <Box sx={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #E85D04, #C9A84C)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 10,
                fontWeight: 700,
                color: 'var(--gs-text-primary)',
                flexShrink: 0
              }}>
                {(build.ownerUsername ?? '?').slice(0, 2).toUpperCase()}
              </Box>
              <Text component={Link} to={`/profiles/${build.ownerUsername}`} size="sm" weight={500}
                sx={{ color: 'var(--gs-text)' }}>
                {build.ownerUsername}
              </Text>
              <Text size="xs" sx={{ color: 'var(--gs-muted)' }}>
                {format(new Date(build.updatedAt), 'MMM d, yyyy')}
              </Text>
              {isOwner && userStore.user?.ammoScore != null && (
                <AmmoScoreBadge
                  tier={getAmmoTier(userStore.user.ammoScore)}
                  score={userStore.user.ammoScore}
                  size="sm"
                />
              )}
            </Group>
          </Box>

          {/* Right: votes + cost + part count + share + edit */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 }}>

            {/* Vote controls */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <Box component="button" type="button" disabled={voteStore.loading} onClick={() => handleVote(1)}
                sx={{
                  background: currentVote === 1 ? 'rgba(63,185,80,0.12)' : 'var(--gs-surface2)',
                  border: '1.5px solid var(--gs-border-bright)',
                  color: currentVote === 1 ? 'var(--gs-green)' : 'var(--gs-text)',
                  borderColor: currentVote === 1 ? 'var(--gs-green)' : undefined,
                  borderRadius: 4,
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'border-color 0.15s, color 0.15s',
                  ...(currentVote !== 1 ? { '&:hover': { borderColor: 'var(--gs-green)', color: 'var(--gs-green)' } } : {})
                }}>
                <IoArrowUpOutline size={16} />
              </Box>
              <Text sx={{
                fontFamily: 'Oswald, sans-serif',
                fontWeight: 700,
                fontSize: 16,
                color: score > 0 ? 'var(--gs-green)' : score < 0 ? 'var(--gs-red)' : 'var(--gs-muted)',
                textAlign: 'center',
                minWidth: 24
              }}>{score}
              </Text>
              <Box component="button" type="button" disabled={voteStore.loading} onClick={() => handleVote(-1)}
                sx={{
                  background: currentVote === -1 ? 'rgba(248,81,73,0.12)' : 'var(--gs-surface2)',
                  border: '1.5px solid var(--gs-border-bright)',
                  color: currentVote === -1 ? 'var(--gs-red)' : 'var(--gs-text)',
                  borderColor: currentVote === -1 ? 'var(--gs-red)' : undefined,
                  borderRadius: 4,
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'border-color 0.15s, color 0.15s',
                  ...(currentVote !== -1 ? { '&:hover': { borderColor: 'var(--gs-red)', color: 'var(--gs-red)' } } : {})
                }}>
                <IoArrowDownOutline size={16} />
              </Box>
            </Box>

            {/* Animated cost display (Phase 3 step 6) */}
            {totalValue > 0 && (
              <Box sx={{ textAlign: 'right' }}>
                <Text size="xs" sx={{ color: 'var(--gs-muted)', fontFamily: 'DM Sans, sans-serif', marginBottom: 1 }}>
                  Total Build Cost
                </Text>
                <span
                  key={totalValue}
                  className="cost-total-display"
                  style={{
                    fontSize: 20,
                    color: 'var(--gs-gold)',
                    letterSpacing: 0.3,
                    animation: 'amberFlash 0.8s ease-out'
                  }}
                >
                  {'$' + animatedCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </Box>
            )}

            {/* Part count */}
            <Group spacing={4} align="center">
              <IoCogOutline size={12} color="var(--gs-muted)" />
              <Text size="xs" sx={{ color: 'var(--gs-muted)', fontFamily: 'DM Sans, sans-serif' }}>
                {partCount} {partCount === 1 ? 'part' : 'parts'}
              </Text>
            </Group>

            {/* Share popover */}
            <Popover opened={shareOpen} onClose={() => setShareOpen(false)}
              position="bottom-end" width={180} withArrow shadow="md">
              <Popover.Target>
                <ActionIcon size="md" variant="subtle" onClick={() => setShareOpen((o) => !o)}
                  sx={{ color: 'var(--gs-muted)', '&:hover': { color: 'var(--gs-text)' } }} title="Share build">
                  <IoShareOutline size={16} />
                </ActionIcon>
              </Popover.Target>
              <Popover.Dropdown sx={{ background: 'var(--gs-surface2)', border: '1px solid var(--gs-border)', padding: 8 }}>
                <Stack spacing={4}>
                  <Button variant="subtle" size="xs" leftIcon={<IoImageOutline size={14} />}
                    loading={sharingImage} onClick={handleDownloadImage}
                    sx={{
                      color: 'var(--gs-text)',
                      justifyContent: 'flex-start',
                      fontFamily: 'DM Sans, sans-serif',
                      '&:hover': { background: 'var(--gs-surface)' }
                    }}>
                    Download Image
                  </Button>
                  <Button variant="subtle" size="xs" leftIcon={<IoLinkOutline size={14} />}
                    onClick={handleCopyLink}
                    sx={{
                      color: 'var(--gs-text)',
                      justifyContent: 'flex-start',
                      fontFamily: 'DM Sans, sans-serif',
                      '&:hover': { background: 'var(--gs-surface)' }
                    }}>
                    Copy Link
                  </Button>
                </Stack>
              </Popover.Dropdown>
            </Popover>

            {/* Edit build details */}
            {isOwner && (
              <ActionIcon size="md" variant="subtle" onClick={() => setEditDetailsOpen(true)}
                sx={{ color: 'var(--gs-muted)', '&:hover': { color: 'var(--gs-text)' } }} title="Edit build details">
                <IoPencilOutline size={16} />
              </ActionIcon>
            )}
          </Box>
        </Group>

        {/* Tags */}
        {tags.length > 0 && (
          <Group spacing="xs" mt={12}>
            {tags.map((t) => (
              <Badge key={t.id} size="sm" variant="outline" radius="sm"
                sx={{ borderColor: '#388BFD44', color: 'var(--gs-blue)' }}>
                {t.displayName || t.name}
              </Badge>
            ))}
          </Group>
        )}
      </Box>

      {/* ── Build completion bar (Phase 3 step 8) ── */}
      <BuildCompletionBar parts={build.parts ?? []} />

      {/* ══ MAIN CONTENT: responsive 2-col (Phase 3 step 11) ════════════════ */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        marginBottom: 28,
        '@media (min-width: 768px)': { flexDirection: 'row', alignItems: 'flex-start' }
      }}>

        {/* ── LEFT: schematic column ── */}
        <Box sx={{
          width: '100%',
          '@media (min-width: 768px)': { width: '55%', flexShrink: 0 },
          '@media (min-width: 1280px)': { width: '50%', flexShrink: 0 }
        }}>
          {/* Zoom/pan outer clip container (Phase 3 step 9) */}
          <Box
            ref={zoomWrapRef}
            sx={{
              overflow: 'hidden',
              borderRadius: 8,
              position: 'relative',
              cursor: zoomScale > 1 ? 'grab' : 'default',
              '&:active': { cursor: zoomScale > 1 ? 'grabbing' : 'default' },
              userSelect: 'none'
            }}
            {...zoomHandlers}
          >
            {/* Reset-zoom button */}
            {zoomScale > 1 && (
              <Box
                component="button"
                type="button"
                onClick={resetZoom}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 20,
                  background: 'rgba(10,14,19,0.82)',
                  border: '1px solid var(--gs-border-bright)',
                  color: 'var(--gs-text)',
                  borderRadius: 4,
                  padding: '2px 8px',
                  fontFamily: 'Oswald, sans-serif',
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: 'pointer',
                  letterSpacing: 0.5,
                  '&:hover': { borderColor: 'var(--gs-accent)', color: 'var(--gs-accent)' }
                }}
              >
                1×
              </Box>
            )}

            {/* Scaled content */}
            <Box sx={{ transform: zoomTransform, transformOrigin: '50% 50%', willChange: 'transform' }}>
              <Box sx={{ position: 'relative' }}>
                {/* Breathing pulse class applied when empty+owner (Phase 3 step 7) */}
                <Box className={isEmpty && isOwner && !firstPartAdded ? 'zone-breathing-pulse' : undefined}>
                  <PlatformSchematic
                    platform={build.firearmModelPlatform}
                    activeZones={activeZones}
                    pulsedZone={pulsedZone}
                    editMode={isOwner}
                    onZoneClick={handleZoneClick}
                    activeZone={activeZone}
                    zoneParts={zoneParts}
                    akIllustrationVariant={getAkIllustrationVariant(build)}
                    containerRefOut={schematicContainerRef}
                  />
                </Box>
                <PartOverlayLayer
                  buildParts={build.parts ?? []}
                  zoneGeometry={zoneGeometry}
                  containerWidth={containerWidth}
                  containerHeight={containerHeight}
                  platform={build.firearmModelPlatform}
                  isOwner={isOwner}
                  activeZone={activeZone}
                  onPartPositionChange={handlePartPositionChange}
                  onPartPositionReset={handlePartPositionReset}
                />

                {/* Empty-state overlay — owner only (Phase 3 step 7) */}
                {isEmpty && isOwner && (
                  <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 10,
                    pointerEvents: 'none',
                    opacity: firstPartAdded ? 0 : 1,
                    transition: 'opacity 400ms ease',
                    textAlign: 'center'
                  }}>
                    <Box sx={{
                      background: 'rgba(0,0,0,0.72)',
                      border: '1px solid rgba(232,93,4,0.6)',
                      borderRadius: 12,
                      padding: '24px 28px'
                    }}>
                      <Text size="sm" weight={600} sx={{ color: 'var(--gs-text)', fontFamily: 'DM Sans, sans-serif', marginBottom: 6 }}>
                        Click any zone on the schematic to start building
                      </Text>
                      {build.firearmModelPlatform && (
                        <Text size="xs" sx={{
                          color: 'var(--gs-accent)',
                          fontFamily: 'Oswald, sans-serif',
                          fontWeight: 700,
                          letterSpacing: 1,
                          textTransform: 'uppercase',
                          marginBottom: 10
                        }}>
                          {build.firearmModelPlatform}
                        </Text>
                      )}
                      <Box sx={{ color: 'var(--gs-accent)', animation: 'bouncingArrow 1.2s infinite ease-in-out', display: 'flex', justifyContent: 'center' }}>
                        <IoArrowDown size={20} />
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* Empty-state text — visitor only */}
          {isEmpty && !isOwner && (
            <Text size="sm" sx={{
              color: 'var(--gs-muted)',
              fontStyle: 'italic',
              textAlign: 'center',
              marginTop: 12,
              fontFamily: 'DM Sans, sans-serif'
            }}>
              This build has no parts yet.
            </Text>
          )}
        </Box>

        {/* ── RIGHT: parts list column ── */}
        <Box sx={{
          flex: 1,
          minWidth: 0,
          /* Keep last card above bottom nav on mobile */
          paddingBottom: 0,
          '@media (max-width: 767px)': { paddingBottom: 64 }
        }}>
          <Group position="apart" mb="md">
            <Text sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600, fontSize: 16 }}>
              PARTS ({partCount})
            </Text>
            {isOwner && (
              <Button size="xs" onClick={() => setAddPartOpen(true)}
                sx={{
                  background: 'var(--gs-surface)',
                  border: '1px solid var(--gs-border)',
                  color: 'var(--gs-text)',
                  '&:hover': { borderColor: 'var(--gs-border-bright)' }
                }}>
                + Add part
              </Button>
            )}
          </Group>

          {/* Mount type selector for AK platforms with Side Rail Mount */}
          {isAkPlatform(build.firearmModelPlatform) && (normalizedPartsByZone['Side Rail Mount'] ?? []).length > 0 && (
            <Box sx={{ background: 'var(--gs-surface2)', border: '1px solid var(--gs-border)', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
              <MountTypeSelector value={currentMountType} onChange={handleMountTypeChange} />
            </Box>
          )}

          {groupedParts.length > 0 ? (
            <Stack spacing="md">
              {groupedParts.map(({ zone, parts: zoneParts2 }) => {
                // For AK platforms: hide Optic zone when Integrated or IronSights is selected
                if (
                  zone === 'Optic' &&
                  isAkPlatform(build.firearmModelPlatform) &&
                  (currentMountType === 'Integrated' || currentMountType === 'IronSights')
                ) {
                  return (
                    <Box key={zone}>
                      <Group position="apart" sx={{ padding: '5px 0', borderBottom: '1px solid var(--gs-border)', marginBottom: 8 }}>
                        <Text size="xs" weight={700} sx={{
                          fontFamily: 'DM Sans, sans-serif',
                          color: 'var(--gs-muted)',
                          textTransform: 'uppercase',
                          letterSpacing: 1
                        }}>
                          Optic
                        </Text>
                      </Group>
                      <Card padding="sm" sx={{
                        background: 'var(--gs-surface2)',
                        border: '1px dashed var(--gs-border)',
                        borderRadius: 8,
                        opacity: 0.7
                      }}>
                        <Text size="sm" sx={{ color: 'var(--gs-muted)', fontStyle: 'italic', textAlign: 'center' }}>
                          {currentMountType === 'Integrated'
                            ? 'Scope included in integrated mount'
                            : 'Iron sights — no optic mounted'}
                        </Text>
                      </Card>
                    </Box>
                  )
                }

                const isExpanded = expandedZones[zone] !== false
                return (
                  <Box key={zone}>
                    {/* Collapsible zone header */}
                    <Group
                      position="apart"
                      onClick={() => toggleZone(zone)}
                      sx={{
                        cursor: 'pointer',
                        padding: '5px 0',
                        borderBottom: '1px solid var(--gs-border)',
                        marginBottom: isExpanded ? 8 : 0,
                        '&:hover': { '& .zone-label': { color: 'var(--gs-text)' } }
                      }}
                    >
                      <Text size="xs" weight={700} className="zone-label" sx={{
                        fontFamily: 'DM Sans, sans-serif',
                        color: 'var(--gs-accent)',
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                        transition: 'color 0.15s'
                      }}>
                        {zone}
                      </Text>
                      <Group spacing={6} align="center">
                        <Badge size="xs" variant="outline"
                          sx={{ color: 'var(--gs-muted)', borderColor: 'var(--gs-border-bright)' }}>
                          {zoneParts2.length}
                        </Badge>
                        {isExpanded
                          ? <IoChevronUpOutline size={12} color="var(--gs-muted)" />
                          : <IoChevronDownOutline size={12} color="var(--gs-muted)" />}
                      </Group>
                    </Group>

                    {/* Part cards — staggered fadeInUp (Phase 3 step 6) */}
                    {isExpanded && (
                      <SimpleGrid cols={1} breakpoints={[{ minWidth: 540, cols: 2 }]} spacing="sm">
                        {zoneParts2.map((bp, index) => (
                          <Card key={bp.id} padding="sm" sx={{
                            background: 'var(--gs-surface)',
                            border: '1px solid var(--gs-border)',
                            borderRadius: 8,
                            transition: 'border-color 150ms',
                            animation: 'fadeInUp 0.18s ease-out both',
                            animationDelay: `${index * 50}ms`,
                            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                            '&:hover': { borderColor: 'var(--gs-border-bright)' }
                          }}>
                            <Group position="apart" align="flex-start" noWrap>
                              <Box sx={{ minWidth: 0 }}>
                                <Group spacing={6} mb={2}>
                                  <IoCogOutline size={13} color="var(--gs-muted)" />
                                  <Text size="sm" weight={600} lineClamp={1}
                                    sx={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--gs-text)' }}>
                                    {bp.part?.name ?? 'Part'}
                                  </Text>
                                  {bp.quantity > 1 && (
                                    <Badge size="xs" variant="outline"
                                      sx={{ color: 'var(--gs-muted)', borderColor: 'var(--gs-border-bright)' }}>
                                      ×{bp.quantity}
                                    </Badge>
                                  )}
                                </Group>
                                {bp.part && (
                                  <Text size="xs" sx={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--gs-muted)' }}>
                                    {[bp.part.manufacturer, bp.part.partNumber].filter(Boolean).join(' · ')}
                                    {bp.part.value > 0 && (
                                      <Text component="span" sx={{ color: 'var(--gs-gold)', marginLeft: 6 }}>
                                        {fmtCurrency(bp.part.value * (bp.quantity ?? 1))}
                                      </Text>
                                    )}
                                  </Text>
                                )}
                                {bp.notes && (
                                  <Text size="xs" sx={{
                                    fontFamily: 'DM Sans, sans-serif',
                                    fontStyle: 'italic',
                                    color: 'var(--gs-muted)',
                                    marginTop: 4
                                  }}>
                                    {bp.notes}
                                  </Text>
                                )}
                              </Box>
                              {isOwner && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
                                  <Box component="button" type="button" onClick={() => openEditPart(bp)}
                                    sx={{
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                      color: 'var(--gs-muted)',
                                      padding: 4,
                                      '&:hover': { color: 'var(--gs-accent)' }
                                    }}
                                    title="Edit part">
                                    <IoPencilOutline size={13} />
                                  </Box>
                                  <Box component="button" type="button" onClick={() => handleRemovePart(bp.partId)}
                                    sx={{
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                      color: 'var(--gs-muted)',
                                      padding: 4,
                                      '&:hover': { color: 'var(--gs-red)' }
                                    }}
                                    title="Remove part">
                                    <IoTrashOutline size={14} />
                                  </Box>
                                </Box>
                              )}
                            </Group>
                          </Card>
                        ))}
                      </SimpleGrid>
                    )}
                  </Box>
                )
              })}
            </Stack>
          ) : (
            <Box sx={{ padding: '24px 0', textAlign: 'center' }}>
              <Text sx={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 14,
                color: 'var(--gs-muted)',
                fontStyle: 'italic'
              }}>
                {isOwner
                  ? 'No parts yet. Click "+ Add part" to build out your parts list.'
                  : 'No parts listed for this build yet.'}
              </Text>
            </Box>
          )}

          {/* Auth CTA (in-column on desktop, between parts and footer on mobile) */}
          {!userStore.isLoggedIn && (
            <Box mt={20}>
              <AuthGateCta variant="banner" action="save this build to your favorites"
                pitch="Join the community to follow builds, vote, and document your own." />
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Tags picker ── */}
      {id && (
        <Box mb={28}>
          <TagPicker targetType={TagTargetType.Build} targetId={id} currentTags={tags}
            onTagApplied={(t: TagDto) => setTags((prev) => [...prev, t])}
            onTagRemoved={(t: TagDto) => setTags((prev) => prev.filter((x) => x.id !== t.id))}
            readOnly={!isOwner}
            aiContext={{ name: build.name, description: build.description ?? undefined }} />
        </Box>
      )}

      {/* ── Description ── */}
      {build.description && (
        <Box mb={28}>
          <Text sx={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 15,
            lineHeight: 1.8,
            color: 'var(--gs-text)',
            whiteSpace: 'pre-wrap'
          }}>
            {build.description}
          </Text>
        </Box>
      )}

      {/* ── Photo gallery ── */}
      <Box mb={28}>
        {photosLoading
          ? (
          <Text size="sm" sx={{ color: 'var(--gs-muted)' }}>Loading photos…</Text>
            )
          : photos.length > 0
            ? (
          <Box>
            <Group position="apart" align="center" mb="sm">
              <Text sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600, fontSize: 14 }}>PHOTOS</Text>
              {canManage && id && (
                <GalleryUploadButton ownerType={ContentPhotoOwnerType.Build} ownerId={id}
                  canUpload={canManage} onSuccess={(p) => { setPhotos((prev) => [...prev, p]); if (id) buildStore.loadBuild(id) }}
                  label="Add photos" />
              )}
            </Group>
            <PhotoGallery photos={photos} canManage={canManage}
              onDelete={handleGalleryDelete} onSetMain={handleSetMain} />
          </Box>
              )
            : canManage && id
              ? (
          <Box sx={{
            textAlign: 'center',
            padding: '24px 16px',
            background: 'var(--gs-surface)',
            borderRadius: 8,
            border: '1px solid var(--gs-border)'
          }}>
            <EmptyState title="No photos yet" message="Add images to showcase this build." />
            <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
              <GalleryUploadButton ownerType={ContentPhotoOwnerType.Build} ownerId={id}
                canUpload={canManage}
                onSuccess={(p) => { setPhotos((prev) => [...prev, p]); if (id) buildStore.loadBuild(id) }}
                label="Upload photos" />
            </Box>
          </Box>
                )
              : null}
      </Box>

      {/* ── Owner delete ── */}
      {isOwner && (
        <Group mt={8} pt={20} sx={{ borderTop: '1px solid var(--gs-border)' }}>
          <Button variant="subtle" size="sm" color="red" leftIcon={<IoTrashOutline size={14} />}
            onClick={() => setDeleteConfirm(true)} sx={{ fontFamily: 'DM Sans, sans-serif' }}>
            Delete build
          </Button>
        </Group>
      )}

      {/* ── Add Part Modal ── */}
      <Modal opened={addPartOpen} onClose={() => setAddPartOpen(false)} title="Add part to build" size="lg">
        <Stack>
          <Group grow>
            <TextInput placeholder="Search parts…" value={partSearch}
              onChange={(e) => setPartSearch(e.currentTarget.value)} />
            <Select data={categoryOptions} value={partCategoryFilter ?? ''}
              onChange={(v) => setPartCategoryFilter(v || null)} placeholder="All categories" />
          </Group>

          {partsLoading
            ? (
            <Text size="sm" color="dimmed">Loading parts…</Text>
              )
            : filteredParts.length === 0
              ? (
            <Text size="sm" color="dimmed">No parts match.</Text>
                )
              : (
            <Box sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid var(--gs-border)', borderRadius: 6 }}>
              {filteredParts.map((p) => (
                <Box key={p.id} onClick={() => {
                  const next = p.id === selectedPartId ? null : p.id
                  setSelectedPartId(next)
                  setPartZoneOverride(next ? getCategoryZone(p.category ?? 'Other') : null)
                }}
                  sx={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    background: selectedPartId === p.id ? 'rgba(232,93,4,0.08)' : 'transparent',
                    borderLeft: selectedPartId === p.id ? '3px solid var(--gs-accent)' : '3px solid transparent',
                    '&:hover': { background: 'var(--gs-surface2)' },
                    transition: 'background 0.1s'
                  }}>
                  <Group position="apart" noWrap>
                    <Box>
                      <Text size="sm" weight={600}>{p.name}</Text>
                      <Text size="xs" color="dimmed">
                        {[p.manufacturer, p.partNumber].filter(Boolean).join(' · ')}
                        {p.category && <> · <Text component="span" sx={{ color: 'var(--gs-accent)' }}>{p.category}</Text></>}
                      </Text>
                    </Box>
                    {p.value > 0 && (
                      <Text size="xs" sx={{ color: 'var(--gs-gold)', fontFamily: 'ui-monospace,monospace', flexShrink: 0 }}>
                        {fmtCurrency(p.value)}
                      </Text>
                    )}
                  </Group>
                </Box>
              ))}
            </Box>
                )}

          <Group grow>
            <NumberInput label="Quantity" min={1} max={99} value={partQty}
              onChange={(v) => setPartQty(typeof v === 'number' ? v : 1)} />
            <Textarea label="Notes (optional)" placeholder="e.g. Torqued to 30 ft-lbs"
              value={partNotes} onChange={(e) => setPartNotes(e.currentTarget.value)} minRows={2} />
          </Group>

          {selectedPartId && (
            <Select
              label="Mounting zone"
              description={'Where this part lives on the schematic. Use \u201cOther\u201d for accessories that don\u2019t attach to a specific location.'}
              data={zoneOrder.map((z) => ({ value: z, label: z }))}
              value={partZoneOverride}
              onChange={setPartZoneOverride}
            />
          )}

          <Group position="right">
            <Button variant="default" onClick={() => setAddPartOpen(false)}>Cancel</Button>
            <Button onClick={handleAddPart} disabled={!selectedPartId} loading={addingPart}
              sx={{ background: 'var(--gs-accent)', color: 'var(--gs-text-primary)', '&:hover': { background: 'var(--gs-accent-dark)' } }}>
              Add to build
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Edit Details Drawer ── */}
      <Drawer opened={editDetailsOpen} onClose={() => setEditDetailsOpen(false)}
        title="Edit build details" position="right" size="md" padding="xl">
        <Stack>
          <TextInput label="Build name" value={editName}
            onChange={(e) => setEditName(e.currentTarget.value)} required />
          <Box>
            <Text size="sm" weight={500} mb={4}>Firearm model</Text>
            <Text size="sm" color="dimmed"
              sx={{
                background: 'var(--gs-surface2)',
                borderRadius: 4,
                padding: '8px 12px',
                border: '1px solid var(--gs-border)'
              }}>
              {build.firearmModelName} — <Text component="span" size="xs">locked after creation</Text>
            </Text>
          </Box>
          <Textarea label="Description" value={editDesc}
            onChange={(e) => setEditDesc(e.currentTarget.value)} minRows={4} autosize />
          <Group position="right" mt="md">
            <Button variant="default" onClick={() => setEditDetailsOpen(false)}>Cancel</Button>
            <Button onClick={handleEditSubmit} loading={editSubmitting}
              disabled={!editName.trim()} color="ember">
              Save changes
            </Button>
          </Group>
        </Stack>
      </Drawer>

      {/* ── Delete Confirm ── */}
      <Modal opened={deleteConfirm} onClose={() => setDeleteConfirm(false)} title="Delete build?">
        <Text size="sm" sx={{ color: 'var(--gs-muted)', marginBottom: 16 }}>
          Remove "{build.name}"? This cannot be undone.
        </Text>
        <Group position="right">
          <Button variant="default" onClick={() => setDeleteConfirm(false)}>Cancel</Button>
          <Button color="red" onClick={handleDelete} loading={buildStore.loading}>Delete</Button>
        </Group>
      </Modal>

      {/* ── Armorer's Bench: zone-interaction panels (Phase 1) ── */}
      {activeZone && (
        <>
          <AddPartPanel
            buildId={id!}
            zone={activeZone}
            open={addPanelOpen}
            onClose={() => { setAddPanelOpen(false); setActiveZone(null) }}
            onPartAdded={handleBuildRefetch}
            currentMountType={currentMountType}
            isAk={isAkPlatform(build.firearmModelPlatform)}
          />
          <ZoneManagePanel
            buildId={id!}
            zone={activeZone}
            parts={normalizedPartsByZone[activeZone] ?? []}
            open={managePanelOpen}
            onClose={() => { setManagePanelOpen(false); setActiveZone(null) }}
            onUpdate={handleBuildRefetch}
            onAddAnother={handleAddAnotherPart}
            currentMountType={currentMountType}
            isAk={isAkPlatform(build.firearmModelPlatform)}
          />
        </>
      )}
      <ZoneViewModal
        zone={activeZone ?? ''}
        parts={activeZone ? (normalizedPartsByZone[activeZone] ?? []) : []}
        open={viewModalOpen}
        onClose={() => { setViewModalOpen(false); setActiveZone(null) }}
      />

      {/* ── Share card (off-screen, for html2canvas) ── */}
      <BuildShareCard ref={shareCardRef} build={build} heroUrl={heroUrl} />

      {/* ── Edit Part Modal ── */}
      <Modal opened={!!editPartId} onClose={() => setEditPartId(null)} title="Edit part" size="sm">
        {editPartId && (() => {
          const bp = build.parts?.find((p) => p.id === editPartId)
          return (
            <Stack>
              <Text size="sm" weight={600} sx={{ color: 'var(--gs-text)' }}>{bp?.part?.name ?? 'Part'}</Text>
              <Group grow>
                <NumberInput label="Quantity" min={1} max={99} value={editPartQty}
                  onChange={(v) => setEditPartQty(typeof v === 'number' ? v : 1)} />
                <Textarea label="Notes" value={editPartNotes}
                  onChange={(e) => setEditPartNotes(e.currentTarget.value)} minRows={2} />
              </Group>
              <Select
                label="Mounting zone"
                description="Where this part appears on the schematic."
                data={zoneOrder.map((z) => ({ value: z, label: z }))}
                value={editPartZone}
                onChange={setEditPartZone}
              />
              {bp?.zoneOverride && bp.zoneOverride !== bp.part?.category && (
                <Text size="xs" sx={{ color: 'var(--gs-muted)', fontStyle: 'italic' }}>
                  Default zone: {bp.part?.category ?? 'Other'}
                </Text>
              )}
              <Group position="right">
                <Button variant="default" onClick={() => setEditPartId(null)}>Cancel</Button>
                <Button onClick={handleSavePart} loading={savingPart}
                  sx={{ background: 'var(--gs-accent)', color: 'var(--gs-text-primary)', '&:hover': { background: 'var(--gs-accent-dark)' } }}>
                  Save
                </Button>
              </Group>
            </Stack>
          )
        })()}
      </Modal>
    </Box>
  )
})
